/* eslint-disable no-console */
/* eslint-disable consistent-return */
import queryString from 'query-string';
import isObject from 'lodash/isObject';
import {
  getNextQuestionSet,
  getMockQuestionSet,
  setDestinationURL,
  triggerAuthAction,
} from './redux/actions';
import { getUid, getChallengeid, getParams } from './util';
import {
  mockAuthQuestions,
  mockInactiveSessionResponse,
  mockNotFoundResponse,
} from './mockResponse';
import { SET_ACTION_MESSAGE, SET_LOGIN_CONFIG } from './redux/types';
import { executeSSOAuthentication } from './util/externalServices';
import router from './util/router';
import LocalStorageServices from './util/localStorageServices';
import SessionStorageServices from './util/sessionStorageServices';
import { checkAuthTypeAndGetAccessToken, getUtmPardotCookie, shouldEnableLogin } from './utils';
import {
  executeDocuSign,
  getTraitsPostAuthorization,
  processPostDocusignStatus,
} from './redux/actions/questions';
import LoggerApi from './LoggerApi';

export default async function initApp(store, config, ssoAuthConfig, identity) {
  const { getState, dispatch } = store;
  const { uid } = getState();
  const { pathname, search } = window.location;
  const URL = pathname.concat(search);
  const ESIGNATURE_ROUTE = '/e-signature';

  const urlUid = getUid(search) || getChallengeid(search);
  const event = getParams('event');
  const documentType = getParams('document_type');
  const identityStatus = identity || SessionStorageServices.getIdentity(getParams('uid'));

  // If identity is `invalid` redirect the user to inactivesession page.
  if (identityStatus === 'invalid' || pathname === '/inactivesession') {
    dispatch(getMockQuestionSet(mockInactiveSessionResponse));
    return;
  }

  // Debug log added to check connection to argus
  // Will remove later
  LoggerApi.info({
    module: 'initApp',
    message: `App loaded in ${process.env.REACT_APP_NODE_ENV}`,
    data: { uid: urlUid },
    path: 'src/initApp.js',
    funcName: 'initApp',
    line: 45,
  });

  /**
   * @description Checks if login is enabled as per carrier's config.
   */
  const { features } = config;
  const { login } = features;
  const { IS_SSO_IDP_INITIATED_FLOW, ENABLE: isSSOEnabled, SSO_USER_TYPE } = ssoAuthConfig || {};
  LocalStorageServices.setSSOConfig(ssoAuthConfig);

  const isSsoLoginRedirected = pathname.includes('/login') && getParams('code');
  const isSSODonePage = pathname.includes('/sso');

  // return if sso auth is in progress with pathname as /login & code param in url
  if (isSsoLoginRedirected || isSSODonePage) return;

  /**
   * @description Sets login related configs in the reducer.
   */
  if (login) {
    dispatch({
      type: SET_LOGIN_CONFIG,
      payload: login,
    });
  }

  const { routes: carrierRoutes = [], productMap = {} } = config;
  const accessToken = await checkAuthTypeAndGetAccessToken();

  const userType = LocalStorageServices.getUserType();
  const queryParams = queryString.parse(search);
  const userRole = (queryParams?.role || '').toLowerCase();

  const isSSOUser = isSsoLoginRedirected ? userType === SSO_USER_TYPE : userRole === SSO_USER_TYPE;
  const showSSOLoginPage = isSSOEnabled ? false : login.enable;
  const isLoginEnabled = !isSSOUser ? shouldEnableLogin(login) : showSSOLoginPage;
  const isLoggedInWithSSO = LocalStorageServices.getLocalValue('isLoggedInWithSSO') || false;

  // Clearing isLoggedInWithSSO in localstorage
  // when switching from SSO app to DTC
  if (userRole !== SSO_USER_TYPE && isLoggedInWithSSO) {
    LocalStorageServices.clearSSOKeys();
  }

  /**
   * In Idp flow, application launches from client's portal and we execute Auth request on /login
   */
  const isIdpInitiatedFlow = IS_SSO_IDP_INITIATED_FLOW && pathname.includes('/login');

  /**
   * In Sp flow, we implement authentication when acquire is launched with SSO enabled role
   */

  const ssoAccessToken = LocalStorageServices.getSSOAccessToken();
  const isSpInitiatedFlow =
    !IS_SSO_IDP_INITIATED_FLOW &&
    !pathname.includes('/login') &&
    userRole === SSO_USER_TYPE &&
    (!ssoAccessToken || (ssoAccessToken && !isLoggedInWithSSO));

  const triggerSSO = isSSOEnabled && (isIdpInitiatedFlow || isSpInitiatedFlow);

  // Clearing accessToken in localstorage to retrigger SSO authentication
  // when switching from DTC app to SSO
  if (triggerSSO) {
    LocalStorageServices.removeLocalValue('accessToken');
  }

  // Enable login page only for non-sso users based on login feature
  let loginRedirection = false;
  if ((!accessToken || (userRole !== SSO_USER_TYPE && isLoggedInWithSSO)) && isLoginEnabled) {
    loginRedirection = pathname !== '/authError';
  }

  // Skip /questions API call if route contains document_type as url param
  if (documentType) {
    return;
  }

  /**
   * @description Redirects the user to loginPage if loginRedirection is true,
   * else gets the access_tokens by making the /auth API call (carrier specific).
   */
  if (loginRedirection) {
    if (!pathname.includes('/login')) {
      dispatch(setDestinationURL(URL));
      dispatch(getMockQuestionSet(mockAuthQuestions));
    } else if (pathname.includes('/login')) {
      dispatch(getMockQuestionSet(mockAuthQuestions));
    }
    return;
  }

  /** Launch SSO authentication */
  if (triggerSSO) {
    SessionStorageServices.setLocalValue('redirectAppPath', URL);
    console.log('Initiating SSO authentication...');
    executeSSOAuthentication(ssoAuthConfig);
    return;
  }

  const makeSSOAuthCall = isSSOEnabled ? false : !accessToken;
  const shouldMakeAuthCall = !isSSOUser
    ? !accessToken || (accessToken && isLoggedInWithSSO)
    : makeSSOAuthCall;

  /** Make /auth call to fetch latest tokens */
  if (shouldMakeAuthCall) {
    console.log('Initiating Auth call on load');
    const validationSuccess = await dispatch(triggerAuthAction());
    if (!validationSuccess) {
      dispatch({
        type: SET_ACTION_MESSAGE,
        payload: {
          actionMessage: {
            type: 'error',
            message: 'Something went wrong!!',
          },
        },
      });
    }
  }

  /** Verify SSO Authorization & get agent details */
  let agentInfo;
  if (isSSOUser && isSSOEnabled && !pathname.includes('/login')) {
    const traits = await getTraitsPostAuthorization({ ssoAuthConfig, dispatch });
    if (traits) {
      agentInfo = traits;
    } else {
      LoggerApi.info({
        module: 'initApp',
        message: 'Traits not found post GET /traits call',
        data: { traits, url: URL },
        path: 'src/initApp.js',
        funcName: 'initApp',
        line: 188,
      });
      return;
    }
  }

  /**
   * if URL does not contain URL, then it's a new application.
   * Change it to /questions connect with APIs
   */
  if (urlUid) {
    if (urlUid !== uid) {
      if (event && event.search('ttl_expired') < 0) {
        const formData = { uid: urlUid, event, next_page_flag: 1 };
        SessionStorageServices.removeRetrySignKeys();
        dispatch(processPostDocusignStatus(formData));
      } else {
        // retry termlife call and then questions call
        const { termlifeApiRetry, termlifeApiPayload } = SessionStorageServices.getRetrySignKeys();
        if (pathname === ESIGNATURE_ROUTE && termlifeApiRetry === 'true') {
          dispatch(executeDocuSign(termlifeApiPayload, { uid: urlUid, ...agentInfo }));
        } else {
          dispatch(getNextQuestionSet('/questions', { uid: urlUid, traits: agentInfo }));
        }
      }
    }
  } else if (pathname === '/init' || pathname === '/' || pathname.includes('/login')) {
    let formData = { traits: agentInfo };
    // Append utm tags and pardot cookie
    if (userRole !== SSO_USER_TYPE) {
      const { utmSource, utmCampaign, visitorID } = getUtmPardotCookie();
      formData = { ...formData, utmSource, utmCampaign, visitorID };
    }

    dispatch(getNextQuestionSet('/questions', formData, {}));
  } else if (carrierRoutes.includes(pathname)) {
    const _queryParams = queryString.parse(search);
    let params = {
      isEmployee: ['/employee', '/employee/'].includes(pathname),
      route: pathname,
      ..._queryParams,
    };
    const route = pathname.split('/')[1];

    // Check and pass product_id key in request
    if (
      isObject(productMap) &&
      Object.keys(productMap).length &&
      Object.keys(productMap).includes(route)
    ) {
      params = {
        ...params,
        product_id: productMap[route],
      };
    }
    router(dispatch, null, false, null);
    dispatch(getNextQuestionSet('/questions', { ...params }, {}));
  } else {
    dispatch(getMockQuestionSet(mockNotFoundResponse));
  }
}
